// photos.service.ts
import axios, { isAxiosError } from "axios";
import api from "../api";
import { literals } from "@/constants/literals";

/** Confirma la subida de una foto (backend espera { imageGroupId, s3Key }). */
export const confirmUpload = async (payload: {
  imageGroupId: number;
  s3Key: string;
}) => {
  await api.post("photos/confirm-upload", payload);
};

/** Sube un fichero con URL firmada. Devuelve payload para confirmación cuando OK. */
type FileWithSuffix = File & { suffix?: number };
export const uploadImage = async (
  file: File | FileWithSuffix,
  imageGroupId: number,
  route?: string,
): Promise<{
  status: boolean;
  filename: string;
  confirmPayload?: { imageGroupId: number; s3Key: string };
}> => {
  try {
    const fileRoute = `${route || ""}${file.name}`;

    const params: any = {
      imageGroupId,
      filename: fileRoute,
      contentType: file.type,
      sizeBytes: file.size,
    };

    if ("suffix" in file && file.suffix !== undefined && file.suffix >= 0) {
      params.suffix = file.suffix;
      params.flow = literals.garage.toLowerCase();
    }

    const { data } = await api.post("photos/presigned-url", params);

    const publicUrl = data.url.replace(
      "http://fotos-componentes-bucket.localstack:4566",
      "http://localhost:4566",
    );

    await axios.put(publicUrl, file, {
      headers: { "Content-Type": data.contentType },
    });

    const s3Key: string = data.s3Key ?? data.key ?? fileRoute;
    return {
      status: true,
      filename: fileRoute,
      confirmPayload: { imageGroupId, s3Key },
    };
  } catch {
    return { status: false, filename: file.name };
  }
};

async function runWithConcurrency<T, R>(
  items: T[],
  worker: (item: T) => Promise<R>,
  concurrency: number,
): Promise<R[]> {
  if (concurrency <= 1) {
    const out: R[] = [];
    for (const item of items) out.push(await worker(item));
    return out;
  }
  const results: R[] = [];
  let index = 0;
  const runners = new Array(Math.min(concurrency, items.length))
    .fill(0)
    .map(async () => {
      while (index < items.length) {
        const current = index++;
        results[current] = await worker(items[current]);
      }
    });
  await Promise.all(runners);
  return results;
}

export const uploadImagesConcurrent = async (
  files: File[],
  imageGroupId: number,
  route?: string,
  options?: {
    concurrency?: number;
    onProgress?: (done: number, total: number) => void;
  },
): Promise<{ ok_files: string[]; ko_files: string[] }> => {
  const ok_files: string[] = [];
  const ko_files: string[] = [];
  let done = 0;

  const concurrency = options?.concurrency ?? 6;
  const onProgress = options?.onProgress;

  await runWithConcurrency(
    files,
    async (file) => {
      const r = await uploadImage(file, imageGroupId, route);
      if (r.status) {
        ok_files.push(r.filename);
        if (r.confirmPayload) {
          await confirmUpload(r.confirmPayload);
        }
      } else {
        ko_files.push(r.filename);
      }
      done += 1;
      onProgress?.(done, files.length); // ✅ sin 'total' variable
      return r;
    },
    concurrency,
  );

  return { ok_files, ko_files };
};

export const getImageSequence = async (
  orderId: number,
  description: string,
) => {
  try {
    const response = await api.get(
      `photos/image-sequence?orderId=${orderId}&description=${description}`,
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      if (error.response.status === 404) {
        throw new Error(literals.notFound);
      }
      throw new Error(error.response.data.message);
    } else {
      throw new Error("Ha habido un problema");
    }
  }
};
